import { AcademicEnrollmentTypeCode, HIGH_SCHOOL_START_TERM } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerUserResponseAnswer,
  CareerQuestion,
  CareerUserResponseSet,
  SchoolLevel,
  SCHOOL_LEVEL,
} from '@backrs_ui/grow-ui/src/models/models';
import { MUTUALLY_EXCLUSIVE_QUESTION_KEYS } from '@backrs_ui/grow-ui/src/utils/questionConstants';

import { QuestionProps } from './molecules/Question';

const SURVEY_STRINGS = appStrings.modules.survey;

export type SurveyStep = 'SCHOOL_LEVEL' | 'ACCOUNT_FIELDS' | 'CAREER';

export const isZipValid = (zip: string | undefined) => /^\d{5}$/.test(zip ?? '');

/**
 * A high schooler is never asked for a start term, and can change school level after answering
 * one, so the term the response set carries is settled from the level rather than the answer.
 */
export const withSettledStartTerm = (
  userResponseSet: CareerUserResponseSet,
): CareerUserResponseSet =>
  userResponseSet.school_level === SCHOOL_LEVEL.HIGH_SCHOOL
    ? { ...userResponseSet, start_term: HIGH_SCHOOL_START_TERM }
    : userResponseSet;

// "None of these" on next_steps is exclusive: picking it clears the rest, picking
// anything else clears it. Returns the selection adjusted for that rule.
export const resolveNextStepsSelection = (
  selectedIds: number[],
  prevIds: number[],
  options: CareerQuestion['options'],
): number[] => {
  const noneOfTheseId = options.find((o) => o.calculator_key === 'next_steps_none_of_these')?.id;

  if (noneOfTheseId === undefined) return selectedIds;

  const justAddedNone = selectedIds.includes(noneOfTheseId) && !prevIds.includes(noneOfTheseId);

  return justAddedNone ? [noneOfTheseId] : selectedIds.filter((id) => id !== noneOfTheseId);
};

const ENROLLMENT_CODE_TO_SCHOOL_LEVEL: Record<AcademicEnrollmentTypeCode, SchoolLevel> = {
  'high-school': SCHOOL_LEVEL.HIGH_SCHOOL,
  'trade-technical': SCHOOL_LEVEL.TRADE,
  'community-college': SCHOOL_LEVEL.COMMUNITY_COLLEGE,
  'college-university': SCHOOL_LEVEL.COLLEGE,
  'graduate-school': SCHOOL_LEVEL.GRADUATE_SCHOOL,
  'non-student': SCHOOL_LEVEL.NO_CURRENT_ENROLLMENT,
  'post-secondary': SCHOOL_LEVEL.COLLEGE,
};

const SCHOOL_LEVEL_TO_ENROLLMENT_CODE: Record<SchoolLevel, AcademicEnrollmentTypeCode> = {
  [SCHOOL_LEVEL.HIGH_SCHOOL]: 'high-school',
  [SCHOOL_LEVEL.TRADE]: 'trade-technical',
  [SCHOOL_LEVEL.COMMUNITY_COLLEGE]: 'community-college',
  [SCHOOL_LEVEL.COLLEGE]: 'college-university',
  [SCHOOL_LEVEL.GRADUATE_SCHOOL]: 'graduate-school',
  [SCHOOL_LEVEL.NO_CURRENT_ENROLLMENT]: 'non-student',
};

export const enrollmentCodeToSchoolLevel = (
  code: AcademicEnrollmentTypeCode | undefined,
): SchoolLevel | undefined => (code ? ENROLLMENT_CODE_TO_SCHOOL_LEVEL[code] : undefined);

export const schoolLevelToEnrollmentCode = (schoolLevel: SchoolLevel): AcademicEnrollmentTypeCode =>
  SCHOOL_LEVEL_TO_ENROLLMENT_CODE[schoolLevel];

const checkIfQuestionShouldSkip = (
  user_responses: CareerUserResponseAnswer[],
  nextQuestion: CareerQuestion,
) => {
  if (!nextQuestion.conditional_logic || nextQuestion.conditional_logic.length === 0) {
    return false;
  }

  // Check for 'SHOW' action: skip only if all conditions fail
  const showLogicFails = nextQuestion.conditional_logic.every((logic) => {
    const { logic_question_id, career_question_option_id, action } = logic;

    // If action is not 'SHOW', we don't consider it here since we only care about HIDE
    if (action !== 'SHOW') {
      return false;
    }

    const responseForCondition = user_responses.some(
      (response) =>
        response.career_question_id === logic_question_id &&
        response.career_question_option_ids.includes(career_question_option_id),
    );

    // If we find a condition where the response exists, don't skip
    return !responseForCondition;
  });

  // Check for 'HIDE' action: skip as soon as any condition is met
  const hideLogicMatches = nextQuestion.conditional_logic.some((logic) => {
    const { logic_question_id, career_question_option_id, action } = logic;

    // If action is not 'HIDE', we don't consider it here
    if (action !== 'HIDE') {
      return false;
    }

    const responseForCondition = user_responses.some(
      (response) =>
        response.career_question_id === logic_question_id &&
        response.career_question_option_ids.includes(career_question_option_id),
    );

    // If we find a condition that matches, we should skip
    return responseForCondition;
  });

  // If all SHOW conditions fail or any HIDE condition matches, we should skip
  return showLogicFails || hideLogicMatches;
};

export const getNextQuestionIndex = ({
  index,
  direction = 'FORWARD',
  userResponseSet,
  addUserQuestionResponse,
  careerQuestions,
}: {
  index: number;
  direction: 'FORWARD' | 'BACKWARD';
  userResponseSet: CareerUserResponseSet;
  addUserQuestionResponse: (response: CareerUserResponseAnswer) => void;
  careerQuestions: CareerQuestion[];
}): number => {
  const nextIndex = direction === 'FORWARD' ? index + 1 : index - 1;

  if (nextIndex < 0 || nextIndex >= careerQuestions.length) {
    return index; // if we are at the end or the beginning, just return index
  }

  const nextQuestion = careerQuestions[nextIndex];

  const { user_responses } = userResponseSet;

  const shouldSkip = checkIfQuestionShouldSkip(user_responses, nextQuestion);

  if (shouldSkip) {
    // if we're skipping a question, we should also clear out any potential response the user had
    const responseToClear = user_responses.find(
      (response) => response.career_question_id === nextQuestion.id,
    );

    if (responseToClear) {
      addUserQuestionResponse({
        id: responseToClear.id,
        career_user_response_set_id: userResponseSet.id,
        career_question_id: nextQuestion.id,
        career_question_option_ids: [], // clear out the option IDs for the response
      });
    }

    // now go to the next question and run the checks again
    return getNextQuestionIndex({
      index: nextIndex,
      direction,
      userResponseSet,
      addUserQuestionResponse,
      careerQuestions,
    });
  }

  return nextIndex;
};

export const schoolLevelQuestionProps = ({
  userResponseSet,
  currentSchoolLevel,
  handleSelectSchoolLevel,
  careerQuestions,
}: {
  userResponseSet: CareerUserResponseSet;
  currentSchoolLevel: SchoolLevel | undefined;
  handleSelectSchoolLevel: (schoolLevel: SchoolLevel) => void;
  careerQuestions: CareerQuestion[];
}): QuestionProps => {
  // we just fake IDs here to get the referencing to work - IDs are required for the logic but don't matter in this case
  const questionID = 10000;
  const highSchoolOptionID = 10001;
  const collegeOptionID = 10002;
  const tradeId = 10003;
  const noCurrentEnrollmentId = 10004;
  const graduateSchoolId = 10005;
  const communityCollegeId = 10006;

  const schoolLevelMap = new Map([
    [highSchoolOptionID, SCHOOL_LEVEL.HIGH_SCHOOL],
    [collegeOptionID, SCHOOL_LEVEL.COLLEGE],
    [tradeId, SCHOOL_LEVEL.TRADE],
    [graduateSchoolId, SCHOOL_LEVEL.GRADUATE_SCHOOL],
    [communityCollegeId, SCHOOL_LEVEL.COMMUNITY_COLLEGE],
    [noCurrentEnrollmentId, SCHOOL_LEVEL.NO_CURRENT_ENROLLMENT],
  ]);

  /** makes the school level options to be displayed in the question */
  function makeOptions() {
    const optionArray = Array.from(schoolLevelMap).map(([optionID, schoolLevel], index) => ({
      id: optionID,
      calculator_key: schoolLevel,
      career_question_id: 0,
      answer: SURVEY_STRINGS.highSchoolCollegeQuestion.options[schoolLevel],
      sequence: index + 1,
    }));

    return optionArray;
  }

  return {
    question: {
      id: questionID,
      title: SURVEY_STRINGS.highSchoolCollegeQuestion.title,
      question: SURVEY_STRINGS.highSchoolCollegeQuestion.question,
      calculator_key: 'school_level', // doesn't matter for this question
      sequence: 0, // used for index and progress bar
      school_level: 'both', // doesn't matter for this question
      options: makeOptions(),
    },
    userResponseSet,
    responsesForQuestion: !currentSchoolLevel
      ? undefined // no response if we haven't selected a school level yet
      : {
          id: `${questionID}`,
          career_user_response_set_id: userResponseSet.id,
          career_question_id: questionID,
          career_question_option_ids: [
            // find the response in the map that matches the current school level so we can display it as selected
            Array.from(schoolLevelMap).find(([, value]) => value === currentSchoolLevel)?.[0],
          ].filter((id) => id !== undefined),
        },
    addUserQuestionResponse: (response) => {
      // we don't update the responses in the response set, but instead update the response set's school level
      const selectedSchoolLevel = Array.from(schoolLevelMap).find(([optionID]) =>
        response.career_question_option_ids.includes(optionID),
      );

      if (selectedSchoolLevel) {
        // we only care about the value and not the key in this case
        const schoolLevel = selectedSchoolLevel[1];

        handleSelectSchoolLevel(schoolLevel);
      }
    },
    careerQuestions,
  };
};

type DetermineIfNextIsDisabledProps = Readonly<{
  isOnSchoolLevelQuestion: boolean;
  currentSchoolLevel: SchoolLevel | undefined;
  currentQuestionIndex: number;
  careerQuestions: CareerQuestion[];
  userResponseSet: CareerUserResponseSet;
}>;

export function determineIfQuestionIsSkippable({
  careerQuestions,
  currentQuestionIndex,
}: {
  careerQuestions: CareerQuestion[];
  currentQuestionIndex: number;
}) {
  if (
    careerQuestions[currentQuestionIndex]?.calculator_key ===
      MUTUALLY_EXCLUSIVE_QUESTION_KEYS.WORK_ENVIRONMENTS_TO_AVOID ||
    careerQuestions[currentQuestionIndex]?.calculator_key ===
      MUTUALLY_EXCLUSIVE_QUESTION_KEYS.ACTIVITIES_YOU_DONT_LIKE
  ) {
    return true;
  }
}

export function determineIfNextIsDisabled({
  isOnSchoolLevelQuestion,
  currentSchoolLevel,
  currentQuestionIndex,
  careerQuestions,
  userResponseSet,
}: DetermineIfNextIsDisabledProps) {
  if (isOnSchoolLevelQuestion) {
    return !currentSchoolLevel;
  }

  const isSkippable = determineIfQuestionIsSkippable({
    careerQuestions,
    currentQuestionIndex,
  });

  if (isSkippable) {
    return false; // Skippable questions are never disabled
  }

  const currentQuestionId = careerQuestions[currentQuestionIndex]?.id;

  const responsesForCurrentQuestion = userResponseSet.user_responses.find(
    (response) => response.career_question_id === currentQuestionId,
  );

  const isNotCustomResponse = !careerQuestions[currentQuestionIndex]?.is_custom_response;

  const hasNoResponse =
    !responsesForCurrentQuestion ||
    responsesForCurrentQuestion?.career_question_option_ids.length === 0;

  return isNotCustomResponse && hasNoResponse;
}

// Helper function to get responses for a specific question by calculator_key
const getResponseForQuestion = (
  userResponseSet: CareerUserResponseSet,
  careerQuestions: CareerQuestion[],
  calculatorKey: string,
): CareerUserResponseAnswer | undefined => {
  const question = careerQuestions.find((q) => q.calculator_key === calculatorKey);

  if (!question) return undefined;

  return userResponseSet.user_responses.find(
    (response) => response.career_question_id === question.id,
  );
};

// Helper function to get selected option texts from a paired question
const getSelectedOptionTextsFromPairedQuestion = (
  userResponseSet: CareerUserResponseSet,
  careerQuestions: CareerQuestion[],
  pairedQuestionKey: string,
): string[] => {
  const pairedResponse = getResponseForQuestion(
    userResponseSet,
    careerQuestions,
    pairedQuestionKey,
  );

  if (!pairedResponse) return [];

  const pairedQuestion = careerQuestions.find((q) => q.calculator_key === pairedQuestionKey);

  if (!pairedQuestion) return [];

  return pairedResponse.career_question_option_ids
    .map((optionId) => {
      const option = pairedQuestion.options.find((opt) => opt.id === optionId);

      return option?.answer;
    })
    .filter((answer): answer is string => answer !== undefined);
};

// Helper function to get mutually exclusive question pairs
const getMutuallyExclusivePairs = (currentQuestionKey: string): string[] => {
  const pairs: Record<string, string[]> = {
    activities_you_enjoy: [MUTUALLY_EXCLUSIVE_QUESTION_KEYS.ACTIVITIES_YOU_DONT_LIKE],
    activities_you_dont_like: [MUTUALLY_EXCLUSIVE_QUESTION_KEYS.ACTIVITIES_YOU_ENJOY],
    preferred_work_environments: [MUTUALLY_EXCLUSIVE_QUESTION_KEYS.WORK_ENVIRONMENTS_TO_AVOID],
    work_environments_to_avoid: [MUTUALLY_EXCLUSIVE_QUESTION_KEYS.PREFERRED_WORK_ENVIRONMENTS],
  };

  return pairs[currentQuestionKey] || [];
};

// Helper function to check if an option should be disabled due to mutual exclusivity
export const isOptionDisabledDueToMutualExclusivity = (
  optionText: string,
  currentQuestionKey: string,
  userResponseSet: CareerUserResponseSet,
  careerQuestions: CareerQuestion[],
): boolean => {
  const mutuallyExclusivePairs = getMutuallyExclusivePairs(currentQuestionKey);

  const res = mutuallyExclusivePairs.some((pairedQuestionKey) => {
    const selectedTexts = getSelectedOptionTextsFromPairedQuestion(
      userResponseSet,
      careerQuestions,
      pairedQuestionKey,
    );

    return selectedTexts.includes(optionText);
  });

  return res;
};

export const getLastBlueprintFormattedDate = (
  userResponseSet: CareerUserResponseSet | undefined | null,
): string => {
  // Safely parse created_at, fallback to today if invalid
  // Pick created_at if valid, else fallback to today
  const rawDate = new Date(userResponseSet?.created_at ?? '');
  const dateToFormat = isNaN(rawDate.getTime()) ? new Date() : rawDate;

  // Format as MM/DD/YYYY
  const formattedDate = [
    (dateToFormat.getMonth() + 1).toString().padStart(2, '0'),
    dateToFormat.getDate().toString().padStart(2, '0'),
    dateToFormat.getFullYear(),
  ].join('/');

  return formattedDate;
};
